import { useNavigate } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";
import SummaryCards from "../../components/Common/SummaryCards";
import {
  Modal,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from "../../components/Common/AppOverlay";
import ActionMenu from "../../components/Common/ActionMenu";
import { showLoading } from "react-global-loading";
import { ErrorNotify } from "../../shared/Alert";
import { getCompanyList } from "../../helpers/backend_helper";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import CompanyDetail from "./CompanyDetail";
import CompanyFullDetail from "./CompanyFullDetail";
import FirmCreateWizard from "./FirmCreateWizard";
import "../Standards/Standards.scss";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.valueOf())
    ? value
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
};

const statusInfo = (status) =>
  status === 1
    ? { label: "Active", tone: "active" }
    : { label: "Inactive", tone: "archived" };

const FirmActionMenu = ({ firm, onEdit, onView }) => {
  const navigate = useNavigate();
  return (
    <ActionMenu
      label={firm.name}
      actions={[
        Number(firm.id) > 1 && { label: "Manage subscription", icon: <i className="bx bx-credit-card" />, onSelect: () => navigate(`/system/subscriptions?firmId=${firm.id}`) },
        {
          label: "Edit details",
          icon: <i className="bx bx-pencil" />,
          onSelect: () => onEdit(firm.id),
        },
        {
          label: "View firm",
          icon: <i className="bx bx-show" />,
          onSelect: () => onView(firm.id),
        },
      ]}
    />
  );
};

const CompanyList = () => {
  const [companies, setCompanies] = useState([]);
  const [detailId, setDetailId] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [fullDetailOpen, setFullDetailOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  const loadData = () => {
    showLoading(true);
    getCompanyList()
      .then((response) => {
        if (response.status) setCompanies(response.data || []);
        else ErrorNotify(response.message);
      })
      .catch((error) => {
        console.error(error);
        ErrorNotify("Error occurred while fetching firms.");
      })
      .finally(() => showLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  const openDetail = (id = 0) => {
    setDetailId(id);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailId(0);
    loadData();
  };

  const closeCreate = () => {
    setCreateOpen(false);
    loadData();
  };

  const openFullDetail = (id) => {
    setDetailId(id);
    setFullDetailOpen(true);
  };

  const closeFullDetail = () => {
    setFullDetailOpen(false);
    setDetailId(0);
    loadData();
  };

  const counts = useMemo(
    () => ({
      total: companies.length,
      active: companies.filter((item) => item.status === 1).length,
      inactive: companies.filter((item) => item.status !== 1).length,
      contactable: companies.filter((item) => item.emailId || item.contactNo)
        .length,
    }),
    [companies]
  );

  const filteredCompanies = useMemo(
    () =>
      companies.filter((item) => {
        const searchText = `${item.name || ""} ${item.address || ""} ${
          item.emailId || ""
        } ${item.contactNo || ""}`.toLowerCase();
        return (
          (!search || searchText.includes(search.toLowerCase())) &&
          (status === "all" || String(item.status) === status)
        );
      }),
    [companies, search, status]
  );

  const resetFilters = () => {
    setSearch("");
    setStatus("all");
  };
  const columns = [
    {
      id: "name",
      header: "Firm",
      accessorKey: "name",
      minSize: 185,
      flex: 1.15,
      cell: ({ row, getValue }) => (
        <button
          type="button"
          className="standards-grid__name"
          style={{ fontSize: 13 }}
          onClick={() => openFullDetail(row.original.id)}
        >
          <span>{(getValue() || "F").charAt(0)}</span>
          {getValue()}
        </button>
      ),
    },
    {
      id: "address",
      header: "Address",
      accessorKey: "address",
      minSize: 220,
      flex: 1.4,
      cell: ({ getValue }) => (
        <span className="standards-grid__description">{getValue() || "—"}</span>
      ),
    },
    {
      id: "contactNo",
      header: "Contact No.",
      accessorKey: "contactNo",
      minSize: 130,
      flex: 0.8,
    },
    {
      id: "emailId",
      header: "Email",
      accessorKey: "emailId",
      minSize: 185,
      flex: 1.05,
    },
    {
      id: "website",
      header: "Website",
      accessorKey: "website",
      minSize: 150,
      flex: 0.9,
      cell: ({ getValue }) => getValue() || "—",
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (row) => statusInfo(row.status).label,
      minSize: 110,
      flex: 0.7,
      cell: ({ row, getValue }) => (
        <span
          className={`standards-grid__status standards-grid__status--${
            statusInfo(row.original.status).tone
          }`}
        >
          {getValue()}
        </span>
      ),
    },
    {
      id: "updatedOn",
      header: "Last Updated",
      accessorFn: (row) => formatDate(row.updatedOn),
      minSize: 145,
      flex: 0.85,
    },
    {
      id: "actions",
      header: "Actions",
      accessorFn: () => "",
      minSize: 80,
      flex: 0,
      enableSorting: false,
      enableColumnFilter: false,
      enableExport: false,
      cell: ({ row }) => (
        <FirmActionMenu
          firm={row.original}
          onEdit={openDetail}
          onView={openFullDetail}
        />
      ),
    },
  ];

  return (
    <div className="page-content standards-page piers-page">
      <div className="container-fluid">
        <header className="standards-page__header piers-page-header">
          <div>
            <h1 className="piers-page-title">Firms</h1>
            <p>
              Manage firms and their contact information across the platform.
            </p>
          </div>
          <button
            type="button"
            className="standards-page__create"
            onClick={() => setCreateOpen(true)}
          >
            <i className="bx bx-plus" /> New Firm
          </button>
        </header>
        <section className="standards-page__content">
          <SummaryCards
            items={[
              [
                "bx-building-house",
                "blue",
                "Total Firms",
                counts.total,
                "All registered firms",
              ],
              [
                "bx-check-circle",
                "green",
                "Active Firms",
                counts.active,
                `${
                  counts.total
                    ? Math.round((counts.active / counts.total) * 100)
                    : 0
                }% of total`,
              ],
              [
                "bx-pause-circle",
                "orange",
                "Inactive Firms",
                counts.inactive,
                `${
                  counts.total
                    ? Math.round((counts.inactive / counts.total) * 100)
                    : 0
                }% of total`,
              ],
              [
                "bx-envelope",
                "red",
                "Contact Details",
                counts.contactable,
                "Firms with contact details",
              ],
            ]}
            ariaLabel="Page summary"
          />
          <section className="standards-grid-panel">
            
            <CustomDataGrid
 filterContent={<section
              className="standards-filter-panel"
              aria-label="Firm filters"
            >
              <div className="standards-filter-panel__fields">
                <label>
                  <span>Search</span>
                  <div className="standards-filter-panel__search">
                    <i className="bx bx-search" />
                    <input
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Search firms..."
                    />
                  </div>
                </label>
                <label>
                  <span>Status</span>
                  <select
                    value={status}
                    onChange={(event) => setStatus(event.target.value)}
                  >
                    <option value="all">All Statuses</option>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </label>
              </div>
              <div className="standards-filter-panel__actions">
                <button type="button" onClick={resetFilters}>Clear</button>
              </div>
            </section>}

              data={filteredCompanies}
              columns={columns}
              onRefresh={loadData}
              fileName="firms"
              recordLabel="firms"
              showToolbar={false}
              initialPageSize={15}
              rowHeight={56}
              columnHeaderHeight={56}
              gridSx={{
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#ffffff !important",
                  borderBottom: "1px solid #e7ebf2",
                },
                "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable":
                  { backgroundColor: "#ffffff !important", color: "#52627f" },
                "& .MuiDataGrid-columnHeaderTitle": {
                  color: "#52627f",
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: ".2px",
                },
                "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
                "& .MuiDataGrid-cell": {
                  fontSize: 13,
                  borderBottomColor: "#edf0f5",
                  color: "#273957",
                },
                "& .MuiDataGrid-row": { minHeight: "56px !important" },
                "& .MuiDataGrid-footerContainer, & .MuiDataGrid-footerContainer .MuiTypography-root":
                  { fontSize: 14, borderTopColor: "#e7ebf2" },
              }}
            />
          </section>
        </section>
        <Offcanvas
          backdrop="static"
          className="standards-drawer"
          direction="end"
          isOpen={detailOpen}
          toggle={closeDetail}
        >
          <OffcanvasHeader toggle={closeDetail} className="canvasHeader">
            Edit Firm
          </OffcanvasHeader>
          <OffcanvasBody>
            <CompanyDetail companyId={detailId} closeAction={closeDetail} />
          </OffcanvasBody>
        </Offcanvas>
        <Offcanvas
          backdrop="static"
          className="firm-create-wizard-drawer"
          direction="end"
          isOpen={createOpen}
          toggle={closeCreate}
        >
          <OffcanvasBody>
            <FirmCreateWizard closeAction={closeCreate} />
          </OffcanvasBody>
        </Offcanvas>
        <Offcanvas
          backdrop="static"
          className="w-90"
          isOpen={fullDetailOpen}
          direction="end"
          toggle={closeFullDetail}
        >
          <OffcanvasHeader toggle={closeFullDetail} className="canvasHeader">
            Firm Details
          </OffcanvasHeader>
          <OffcanvasBody>
            <CompanyFullDetail
              companyId={detailId}
              closeAction={closeFullDetail}
            />
          </OffcanvasBody>
        </Offcanvas>
      </div>
    </div>
  );
};

export default CompanyList;
